import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import client from './api/client';
import UserProfile from './UserProfile';
import SeedingPage from './SeedingPage';
import SeedingDetailPage from './SeedingDetailPage';
import SettingsPage from './SettingsPage';
import LPTrackingPage from './LPTrackingPage';
import SkillFarmPage from './SkillFarmPage';
import DealAnalysisPage from './DealAnalysisPage';
import PriceAnalysisPage from './PriceAnalysisPage';
import MarketActivityPage from './MarketActivityPage';
import ContractSeedingPage from './ContractSeedingPage';
import ContractSeedingDetailPage from './ContractSeedingDetailPage';
import ReportsPage from './ReportsPage';
import InvestmentsPage from './InvestmentsPage';
import Navigation from './Navigation';
import PageHeader from './components/PageHeader';
import './App.css';

function TopItems({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="panel">
      <div className="panel-head"><h2>{title}</h2></div>
      <table className="data-table">
        <tbody>
          {items.slice(0, 5).map((item, index) => (
            <tr key={item.type_id}>
              <td className="text-faint home-rank">{index + 1}</td>
              <td>{item.type_name}</td>
              <td className="num">{item.price?.toLocaleString()} ISK</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function MainPage() {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Store last updated timestamp for market data
  const [marketLastUpdated, setMarketLastUpdated] = useState(null);
  
  // Calculate time elapsed since market data last update
  const calculateTimeElapsed = useCallback((lastUpdated) => {
    if (!lastUpdated) return 0;
    const elapsed = Math.floor((Date.now() - lastUpdated) / 1000);
    return Math.max(0, elapsed);
  }, []);

  const [marketElapsedTime, setMarketElapsedTime] = useState(0);


  const fetchMarketData = useCallback(async () => {
    try {
      const response = await client.get('/eve/market/summary/');
      setMarketData(response.data);
      localStorage.setItem('marketData', JSON.stringify(response.data));
      
      if (response.data.last_updated !== undefined) {
        // Convert server timestamp to milliseconds and store it
        const lastUpdatedMs = response.data.last_updated * 1000; // Convert from seconds to milliseconds
        setMarketLastUpdated(lastUpdatedMs);
        localStorage.setItem('marketLastUpdated', lastUpdatedMs.toString());
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
    }
  }, []);


  // Effect for INITIAL LOAD only. Runs ONCE on mount.
  useEffect(() => {
    const initialLoad = async () => {
      // Set loading to true at the very start
      setLoading(true);

      const cachedMarketData = localStorage.getItem('marketData');

      // Attempt to load from cache for an instant UI
      if (cachedMarketData) {
        const parsedMarketData = JSON.parse(cachedMarketData);
        setMarketData(parsedMarketData);

        // Restore market last updated time from localStorage
        const storedMarketLastUpdated = localStorage.getItem('marketLastUpdated');

        if (storedMarketLastUpdated) {
          const marketLastUpdatedTime = parseInt(storedMarketLastUpdated);
          setMarketLastUpdated(marketLastUpdatedTime);
          setMarketElapsedTime(calculateTimeElapsed(marketLastUpdatedTime));
        }
      }

      // Always fetch fresh data from the API on initial load
      try {
        await fetchMarketData();
      } catch (error) {
        console.error('Error during initial data fetch:', error);
      } finally {
        // Set loading to false after all fetches are complete
        setLoading(false);
      }
    };

    initialLoad();
  }, [calculateTimeElapsed, fetchMarketData]);

  // Note: Removed periodic refresh timers - now handled by server-synchronized countdown expiration

  // Effect for the VISUAL timer updates
  useEffect(() => {
    const timerInterval = setInterval(() => {
      // Update market elapsed time based on last updated timestamp
      const marketElapsed = calculateTimeElapsed(marketLastUpdated);
      setMarketElapsedTime(marketElapsed);

      // Note: Market data refresh now handled by backend cronjob every 10 minutes
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [marketLastUpdated, calculateTimeElapsed]);



  const formatTimeElapsed = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return m > 0 ? `${m}m ${seconds % 60}s` : `${seconds}s`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(typeof timestamp === 'string' ? timestamp : timestamp * 1000);
    return date.toLocaleString();
  };

  const handleRefresh = () => {
    fetchMarketData();
  }

  if (loading) {
    return (
      <div className="page">
        <div className="page-status">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Jita Market"
        meta={marketLastUpdated && [
          `Updated ${formatTimeElapsed(marketElapsedTime)} ago`,
          formatDate(marketData?.last_updated),
        ]}
      >
        <button onClick={handleRefresh} className="btn">Refresh</button>
      </PageHeader>
      <div className="page-content">
        {marketData && marketData.status && marketData.status.startsWith('Data available') ? (
          <>
            <div className="kpis">
              <div className="kpi">
                <span>Total orders</span>
                <b>{marketData.total_orders?.toLocaleString() || '0'}</b>
              </div>
              <div className="kpi">
                <span>Buy orders</span>
                <b>{marketData.buy_orders?.toLocaleString() || '0'}</b>
              </div>
              <div className="kpi">
                <span>Sell orders</span>
                <b>{marketData.sell_orders?.toLocaleString() || '0'}</b>
              </div>
            </div>

            <div className="home-lists">
              <TopItems title="Highest sell orders" items={marketData.highest_priced_items} />
              <TopItems title="Highest buy orders" items={marketData.highest_buy_orders} />
            </div>
          </>
        ) : (
          <div className="page-status">{marketData?.error || 'Market data not available yet.'}</div>
        )}
      </div>
    </div>
  );
}

// Each section of the site owns a hue (nav marker, page header band, primary buttons)
const AREAS = [
  ['/seeding', 'seeding'],
  ['/contracts', 'contracts'],
  ['/lp-tracking', 'lp'],
  ['/reports', 'reports'],
  ['/investments', 'investments'],
  ['/skillfarm', 'analysis'],
  ['/deals', 'analysis'],
  ['/price-analysis', 'analysis'],
  ['/market-activity', 'analysis'],
];

function Shell() {
  const { pathname } = useLocation();
  const area = AREAS.find(([prefix]) => pathname.startsWith(prefix))?.[1];

  return (
    <div style={area ? { '--area': `var(--area-${area})` } : undefined}>
      <Navigation />
      <Routes>
        <Route path="/" element={<MainPage />} />
        <Route path="/seeding" element={<SeedingPage />} />
        <Route path="/seeding/:destinationId" element={<SeedingDetailPage />} />
        <Route path="/lp-tracking" element={<LPTrackingPage />} />
        <Route path="/contracts" element={<ContractSeedingPage />} />
        <Route path="/contracts/:destinationId" element={<ContractSeedingDetailPage />} />
        <Route path="/skillfarm" element={<SkillFarmPage />} />
        <Route path="/deals" element={<DealAnalysisPage />} />
        <Route path="/price-analysis" element={<PriceAnalysisPage />} />
        <Route path="/market-activity" element={<MarketActivityPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/investments" element={<InvestmentsPage />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Shell />
    </Router>
  );
}

export default App;